// ---- helpers -------------------------------------------------------------

export function slugify(str) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

// Deterministic pseudo-random generator so the same track always renders
// the same waveform / duration between page loads.
function seededRandom(seed) {
  let t = seed + 0x6d2b79f5;
  return function () {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function formatDuration(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = Math.round(totalSeconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function artistAvatar(name) {
  return `https://picsum.photos/seed/mu-avatar-${slugify(name)}/60/60`;
}

const labels = ["Independent", "Untamed Collective", "Culture Haus Records", "Lagos Sound Label"];

const descriptionTemplates = {
  Afrobeats: (title, artist) =>
    `A genre-bending Afrobeats anthem that fuses hypnotic percussion with ${artist}'s signature vocal glide — the track that carried the sound worldwide.`,
  "R&B": (title, artist) =>
    `A smooth, soul-drenched cut from ${artist}, built on warm chords and an unhurried, late-night vocal performance.`,
  "Hip-Hop": (title, artist) =>
    `A hard-hitting hip-hop record where ${artist} trades melody for menace over a stripped-back, bass-heavy beat.`,
  Amapiano: (title, artist) =>
    `A log-drum-driven amapiano groove from ${artist}, built for the dance floor and built to last all night.`,
  Afropop: (title, artist) =>
    `A bright, hook-forward afropop single from ${artist}, layering breezy melodies over a sun-soaked rhythm.`,
  Gospel: (title, artist) =>
    `An uplifting gospel record from ${artist}, blending choral harmonies with contemporary production.`,
  Alte: (title, artist) =>
    `An experimental alté cut from ${artist}, mixing left-field production with genre-agnostic songwriting.`,
  Highlife: (title, artist) =>
    `A modern highlife record from ${artist}, honoring the genre's guitar-driven roots with a contemporary sheen.`
};

function buildDescription(title, artist, genre) {
  const template = descriptionTemplates[genre] || descriptionTemplates.Afrobeats;
  return template(title, artist);
}

// ---- raw catalog ----------------------------------------------------------
// "tag" marks where a track first appears (trending / newRelease / catalog)
// so Home and Music pages can pull the right subset from one source of truth.

const raw = [
  // Homepage "Trending Right Now"
  { title: "Calm Down", artist: "Rema", genre: "Afrobeats", releaseDate: "Jul 2026", tag: "trending",
    label: "Independent", durationSeconds: 192,
    description: "A genre-bending Afrobeats anthem that fuses hypnotic percussion with Rema's signature vocal glide — the track that carried the sound worldwide." },
  { title: "Essence", artist: "Wizkid ft. Tems", genre: "R&B", releaseDate: "Jul 2026", tag: "trending" },
  { title: "Kilometre", artist: "Odunodubvck", genre: "Hip-Hop", releaseDate: "Jun 2026", tag: "trending" },
  { title: "Unavailable", artist: "Davido ft. Musa Keys", genre: "Amapiano", releaseDate: "Jun 2026", tag: "trending" },

  // Music page "New Releases"
  { title: "Soso", artist: "Omah Lay", genre: "Afrobeats", releaseDate: "Aug 2026", tag: "newRelease" },
  { title: "Terminator", artist: "Odumodublvck", genre: "Hip-Hop", releaseDate: "Aug 2026", tag: "newRelease" },
  { title: "Sability", artist: "Asake", genre: "Afrobeats", releaseDate: "Aug 2026", tag: "newRelease" },
  { title: "Marry Me", artist: "Ayra Starr", genre: "Afropop", releaseDate: "Aug 2026", tag: "newRelease" },
  { title: "Marry Me (Remix)", artist: "Ayra Starr", genre: "Afropop", releaseDate: "Aug 2026", tag: "newRelease" },
  { title: "Higher", artist: "Tems", genre: "R&B", releaseDate: "Aug 2026", tag: "newRelease" }
];

// Fill out the "All Music" catalog the same way the mock UI repeats sample
// cards to demonstrate a full browsable grid.
const catalogNames = ["Dumebi", "Woman", "Charm", "Are You There"];
const catalogGenres = ["Afrobeats", "Amapiano", "R&B", "Hip-Hop", "Afropop", "Gospel", "Alte", "Highlife"];
const catalogMonths = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug"];

for (let i = 0; i < 24; i++) {
  raw.push({
    title: catalogNames[i % catalogNames.length],
    artist: "Rema",
    genre: catalogGenres[i % catalogGenres.length],
    releaseDate: `${catalogMonths[i % catalogMonths.length]} 2026`,
    tag: "catalog"
  });
}

// ---- resolved catalog -------------------------------------------------

export const tracks = raw.map((t, i) => {
  const id = i + 1;
  const slug = `${slugify(t.title)}-${id}`;
  const rand = seededRandom(id * 97);
  const durationSeconds = t.durationSeconds || Math.round(150 + rand() * 100); // ~2:30–4:10
  const waveform = Array.from({ length: 40 }, () => 0.25 + seededRandom(id * 31 + Math.round(rand() * 1000))() * 0.75);

  return {
    id,
    slug,
    title: t.title,
    artist: t.artist,
    genre: t.genre,
    tag: t.tag,
    releaseDate: t.releaseDate,
    label: t.label || labels[id % labels.length],
    duration: formatDuration(durationSeconds),
    durationSeconds,
    img: `https://picsum.photos/seed/mu-track-${slug}/500/500`,
    artistAvatar: artistAvatar(t.artist.split(" ft. ")[0]),
    description: t.description || buildDescription(t.title, t.artist, t.genre),
    waveform
  };
});

export const trendingTracks = tracks.filter((t) => t.tag === "trending");
export const newReleaseTracks = tracks.filter((t) => t.tag === "newRelease");
export const allMusicTracks = tracks.filter((t) => t.tag === "catalog");
export const totalTrackCount = 240;

export function getTrackBySlug(slug) {
  return tracks.find((t) => t.slug === slug);
}

// Prefer other tracks by the same artist; fall back to same genre, then
// anything else, so every track detail page can always fill 6 cards.
export function getMoreFromArtist(track, count = 6) {
  const sameArtist = tracks.filter((t) => t.id !== track.id && t.artist === track.artist);
  const sameGenre = tracks.filter(
    (t) => t.id !== track.id && t.artist !== track.artist && t.genre === track.genre
  );
  const rest = tracks.filter(
    (t) => t.id !== track.id && t.artist !== track.artist && t.genre !== track.genre
  );
  return [...sameArtist, ...sameGenre, ...rest].slice(0, count);
}

// Deterministic "you might also like" picks (based on track id) so the
// selection stays stable between renders instead of reshuffling.
export function getYouMightAlsoLike(track, exclude = [], count = 6) {
  const excludeIds = new Set([track.id, ...exclude.map((t) => t.id)]);
  const pool = tracks.filter((t) => !excludeIds.has(t.id));
  const offset = track.id % pool.length;
  const rotated = [...pool.slice(offset), ...pool.slice(0, offset)];
  return rotated.slice(0, count);
}
